import React, { useEffect, useState } from 'react';
import './CareersPage.css';

const JOBS = [
  {
    id: 1,
    title: 'Sales Executive',
    department: 'Sales',
    location: 'Hyderabad / Vijayawada',
    type: 'Full-time',
    experience: '1–3 Years',
    icon: 'fa-handshake',
    summary:
      'Drive growth by onboarding new vehicle dealers and buyers onto the VahaanBazar platform.',
    responsibilities: [
      'Identify and onboard new dealers, buyers, and sellers in the assigned territory',
      'Conduct on-ground visits to vehicle yards, dealerships, and auctions',
      'Achieve monthly targets for vehicle listings and transactions',
      'Build and maintain strong client relationships',
      'Coordinate with operations team for smooth deal closures',
    ],
    requirements: [
      '1–3 years of experience in field sales (automotive preferred)',
      'Excellent communication in Telugu and English',
      'Own two-wheeler and valid driving licence',
      'Target-oriented with strong negotiation skills',
      'Willingness to travel within the assigned region',
    ],
  },
  {
    id: 2,
    title: 'Digital Marketing Manager',
    department: 'Marketing',
    location: 'Hyderabad',
    type: 'Full-time',
    experience: '3–5 Years',
    icon: 'fa-bullhorn',
    summary:
      "Lead VahaanBazar's digital presence across social media, SEO, and performance marketing.",
    responsibilities: [
      'Plan and execute digital campaigns across Google Ads, Meta, and YouTube',
      'Manage SEO/SEM strategy to grow organic traffic',
      'Oversee social media content calendar and community management',
      'Analyse marketing metrics and optimise campaigns for ROI',
      'Collaborate with design and tech teams on creatives and landing pages',
    ],
    requirements: [
      '3–5 years of digital marketing experience',
      'Proficiency in Google Ads, Meta Ads Manager, and analytics tools',
      'Strong understanding of SEO/SEM best practices',
      'Experience in automotive or marketplace industry is a plus',
      'Data-driven mindset with strong analytical skills',
    ],
  },
  {
    id: 3,
    title: 'Field Operations Executive',
    department: 'Operations',
    location: 'Multiple Locations (AP & Telangana)',
    type: 'Full-time',
    experience: '1–2 Years',
    icon: 'fa-truck',
    summary:
      'Coordinate vehicle inspections, logistics, and on-ground operations at franchise locations.',
    responsibilities: [
      'Oversee vehicle inspection and valuation processes at the yard',
      'Coordinate RTO documentation and transfer formalities',
      'Manage inventory listing accuracy on the platform',
      'Liaise with franchise partners for day-to-day operations',
      'Ensure smooth auction events and buyer-seller coordination',
    ],
    requirements: [
      '1–2 years of operations/logistics experience',
      'Basic knowledge of commercial vehicles and construction equipment',
      'Proficiency in MS Office and mobile apps',
      'Strong organisational and problem-solving abilities',
      'Willingness to travel across AP & Telangana',
    ],
  },
  {
    id: 4,
    title: 'Full Stack Developer',
    department: 'Technology',
    location: 'Hyderabad (Hybrid)',
    type: 'Full-time',
    experience: '2–4 Years',
    icon: 'fa-code',
    summary:
      'Build and scale the VahaanBazar marketplace platform and mobile app features.',
    responsibilities: [
      'Develop and maintain React.js front-end features for the web platform',
      'Build robust REST APIs and backend services',
      'Collaborate with design team to implement pixel-perfect UI',
      'Optimise application performance and scalability',
      'Participate in code reviews and sprint planning',
    ],
    requirements: [
      '2–4 years of full-stack development experience',
      'Strong proficiency in React.js, Node.js, and REST APIs',
      'Experience with databases (MySQL / PostgreSQL / MongoDB)',
      'Familiarity with cloud platforms (AWS / GCP)',
      'Good problem-solving skills and attention to detail',
    ],
  },
  {
    id: 5,
    title: 'Customer Support Executive',
    department: 'Support',
    location: 'Hyderabad',
    type: 'Full-time',
    experience: '0–2 Years',
    icon: 'fa-headset',
    summary:
      'Deliver exceptional customer experience to buyers, sellers, and franchise partners on the platform.',
    responsibilities: [
      'Handle inbound calls, emails, and chat queries from users',
      'Resolve complaints and escalate complex issues to relevant teams',
      'Maintain accurate records of all customer interactions',
      'Follow up with customers to ensure issue resolution',
      'Provide feedback to improve product and service quality',
    ],
    requirements: [
      '0–2 years of customer support experience',
      'Excellent communication in Telugu, Hindi, and English',
      'Patient, empathetic, and solution-oriented mindset',
      'Familiarity with CRM tools is a plus',
      'Ability to handle high-volume queries efficiently',
    ],
  },
];

const EMPTY_FORM = {
  fullName: '',
  email: '',
  phone: '',
  city: '',
  position: '',
  experience: '',
  company: '',
  linkedin: '',
  about: '',
  resume: null,
};

function CareersPage() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [expandedJob, setExpandedJob] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [resumeName, setResumeName] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // Fonts
    const f = document.createElement('link');
    f.href =
      'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=Poppins:wght@400;500;600;700;800&display=swap';
    f.rel = 'stylesheet';
    document.head.appendChild(f);

    // Icons
    const fa = document.createElement('link');
    fa.href =
      'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css';
    fa.rel = 'stylesheet';
    document.head.appendChild(fa);

    window.scrollTo(0, 0);

    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);

    const els = document.querySelectorAll(
      '.car .reveal, .car .reveal-left, .car .reveal-right'
    );
    const obs = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add('shown');
        }),
      { threshold: 0.06, rootMargin: '0px 0px -30px 0px' }
    );
    els.forEach((el) => obs.observe(el));

    return () => {
      window.removeEventListener('scroll', onScroll);
      obs.disconnect();
    };
  }, []);

  const go = (e, id) => {
    e.preventDefault();
    document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  const toggleJob = (id) => setExpandedJob((prev) => (prev === id ? null : id));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['application/pdf', 'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) {
      setErrors((prev) => ({ ...prev, resume: 'Please upload a PDF or Word document.' }));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, resume: 'File size must be under 5 MB.' }));
      return;
    }
    setFormData((prev) => ({ ...prev, resume: file }));
    setResumeName(file.name);
    setErrors((prev) => ({ ...prev, resume: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.fullName.trim()) e.fullName = 'Full name is required.';
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email))
      e.email = 'A valid email is required.';
    if (!formData.phone.trim() || !/^\d{10}$/.test(formData.phone.replace(/\s/g, '')))
      e.phone = 'Enter a valid 10-digit phone number.';
    if (!formData.city.trim()) e.city = 'City is required.';
    if (!formData.position) e.position = 'Please select a position.';
    if (!formData.experience.trim()) e.experience = 'Experience is required.';
    if (!formData.about.trim()) e.about = 'Please write a brief note about yourself.';
    if (!formData.resume) e.resume = 'Please attach your resume.';
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      document.querySelector('.car-form-section')?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    setSubmitting(true);
    // Simulate submission — integrate with your backend or email service here
    setTimeout(() => {
      setSubmitting(false);
      setSubmitted(true);
      setFormData(EMPTY_FORM);
      setResumeName('');
    }, 1200);
  };

  return (
    <div className="car">

      {/* ── NAV ── */}
      <nav className={`car-topbar ${scrolled ? 'stuck' : ''}`}>
        <a href="/" className="car-topbar-logo">
          <img src="https://vahaan-buy-and-sell-category-images.s3.ap-south-1.amazonaws.com/VahaanBazar_Image.png" alt="VahaanBazar" />
        </a>
        <ul className={`car-topbar-nav ${menuOpen ? 'open' : ''}`}>
          <li><a href="#car-jobs" onClick={(e) => go(e, '#car-jobs')}>Open Roles</a></li>
          <li><a href="#car-culture" onClick={(e) => go(e, '#car-culture')}>Culture</a></li>
          <li>
            <a href="#car-apply" className="car-topbar-cta" onClick={(e) => go(e, '#car-apply')}>
              Apply Now
            </a>
          </li>
        </ul>
        <button className="car-topbar-toggle" onClick={() => setMenuOpen(!menuOpen)}>
          <i className={menuOpen ? 'fas fa-times' : 'fas fa-bars'}></i>
        </button>
      </nav>

      {/* ── HERO ── */}
      <section className="car-hero" id="car-hero">
        <div className="car-hero-inner">
          <div className="car-hero-eyebrow car-hero-anim">
            <span>Careers at VahaanBazar</span>
          </div>
          <h1 className="car-hero-anim">
            Build the future of <em>India's vehicle marketplace</em>
          </h1>
          <p className="car-hero-lead car-hero-anim">
            Join a passionate team transforming how India buys and sells commercial vehicles.
            We're growing fast and looking for driven people to grow with us.
          </p>
          <div className="car-hero-stats car-hero-anim">
            <div className="car-hero-stat">
              <strong>{JOBS.length}</strong>
              <span>Open Positions</span>
            </div>
            <div className="car-hero-stat">
              <strong>Hyderabad</strong>
              <span>Headquarters</span>
            </div>
            <div className="car-hero-stat">
              <strong>Pan-India</strong>
              <span>Presence</span>
            </div>
          </div>
          <div className="car-hero-btns car-hero-anim">
            <a href="#car-jobs" className="car-btn car-btn-dark" onClick={(e) => go(e, '#car-jobs')}>
              <i className="fas fa-briefcase"></i> View Open Roles
            </a>
            <a href="#car-apply" className="car-btn car-btn-soft" onClick={(e) => go(e, '#car-apply')}>
              <i className="fas fa-paper-plane"></i> Apply Now
            </a>
          </div>
        </div>
      </section>

      {/* ── CULTURE ── */}
      <section className="car-sec car-culture-sec" id="car-culture">
        <div className="car-wrap">
          <div className="car-sec-header centered reveal">
            <span className="car-sec-eyebrow">Life at VahaanBazar</span>
            <h2 className="car-sec-title">Why work <em>with us?</em></h2>
            <p className="car-sec-sub">
              We're on a mission to bring trust, technology, and transparency to India's
              used vehicle market. Here's what makes working here special.
            </p>
          </div>
          <div className="car-perks-grid">
            {[
              { icon: 'fa-rocket', title: 'High-Growth Environment', desc: 'Be part of a startup scaling rapidly across AP & Telangana with plans for nationwide expansion.' },
              { icon: 'fa-lightbulb', title: 'Ownership & Impact', desc: 'Your work directly shapes product decisions, operations, and customer outcomes. No bureaucracy.' },
              { icon: 'fa-users', title: 'Collaborative Team', desc: 'Work alongside passionate professionals from automotive, tech, and operations backgrounds.' },
              { icon: 'fa-graduation-cap', title: 'Learning & Development', desc: 'Access ongoing training, industry knowledge, and mentorship to accelerate your career.' },
              { icon: 'fa-map-marker-alt', title: 'Pan-India Exposure', desc: 'Gain experience across multiple markets with on-ground operations in AP & Telangana.' },
              { icon: 'fa-trophy', title: 'Performance Rewards', desc: 'Competitive salaries, performance bonuses, and recognition programs that celebrate wins.' },
            ].map((p, i) => (
              <div className={`car-perk reveal s${i + 1}`} key={i}>
                <div className="car-perk-icon">
                  <i className={`fas ${p.icon}`}></i>
                </div>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── OPEN POSITIONS ── */}
      <section className="car-sec car-jobs-sec" id="car-jobs">
        <div className="car-wrap">
          <div className="car-sec-header centered reveal">
            <span className="car-sec-eyebrow">Open Positions</span>
            <h2 className="car-sec-title">Find your <em>next role</em></h2>
            <p className="car-sec-sub">
              Click on any position to view the full job description, then scroll down to apply.
            </p>
          </div>
          <div className="car-jobs-list">
            {JOBS.map((job, i) => (
              <div
                className={`car-job-card reveal s${i + 1} ${expandedJob === job.id ? 'open' : ''}`}
                key={job.id}
              >
                <button className="car-job-header" onClick={() => toggleJob(job.id)}>
                  <div className="car-job-icon">
                    <i className={`fas ${job.icon}`}></i>
                  </div>
                  <div className="car-job-meta">
                    <h3>{job.title}</h3>
                    <div className="car-job-tags">
                      <span className="car-tag"><i className="fas fa-layer-group"></i> {job.department}</span>
                      <span className="car-tag"><i className="fas fa-map-marker-alt"></i> {job.location}</span>
                      <span className="car-tag"><i className="fas fa-clock"></i> {job.type}</span>
                      <span className="car-tag car-tag-exp"><i className="fas fa-chart-line"></i> {job.experience}</span>
                    </div>
                  </div>
                  <div className="car-job-toggle">
                    <i className={`fas ${expandedJob === job.id ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
                  </div>
                </button>

                {expandedJob === job.id && (
                  <div className="car-job-body">
                    <p className="car-job-summary">{job.summary}</p>
                    <div className="car-job-details">
                      <div className="car-job-detail-col">
                        <h4><i className="fas fa-tasks"></i> Responsibilities</h4>
                        <ul>
                          {job.responsibilities.map((r, ri) => <li key={ri}>{r}</li>)}
                        </ul>
                      </div>
                      <div className="car-job-detail-col">
                        <h4><i className="fas fa-check-circle"></i> Requirements</h4>
                        <ul>
                          {job.requirements.map((r, ri) => <li key={ri}>{r}</li>)}
                        </ul>
                      </div>
                    </div>
                    <a
                      href="#car-apply"
                      className="car-btn car-btn-dark car-apply-link"
                      onClick={(e) => {
                        go(e, '#car-apply');
                        setFormData((prev) => ({ ...prev, position: job.title }));
                      }}
                    >
                      <i className="fas fa-paper-plane"></i> Apply for this Role
                    </a>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── APPLICATION FORM ── */}
      <section className="car-sec car-form-section" id="car-apply">
        <div className="car-wrap">
          <div className="car-sec-header centered reveal">
            <span className="car-sec-eyebrow">Apply Now</span>
            <h2 className="car-sec-title">Submit your <em>application</em></h2>
            <p className="car-sec-sub">
              Fill in your details and attach your resume. We review every application and
              will get back to you within 5–7 working days.
            </p>
          </div>

          {submitted ? (
            <div className="car-success reveal">
              <div className="car-success-icon">
                <i className="fas fa-check-circle"></i>
              </div>
              <h3>Application Received!</h3>
              <p>
                Thank you for your interest in VahaanBazar. Our team will review your application
                and reach out to you within 5–7 working days.
              </p>
              <p className="car-success-note">
                Questions? Email us at{' '}
                <a href="mailto:careers@vahaanbazar.com">careers@vahaanbazar.com</a>
              </p>
              <button className="car-btn car-btn-dark" onClick={() => setSubmitted(false)}>
                <i className="fas fa-redo"></i> Submit Another Application
              </button>
            </div>
          ) : (
            <form className="car-form reveal" onSubmit={handleSubmit} noValidate>
              <div className="car-form-grid">

                {/* Full Name */}
                <div className={`car-field ${errors.fullName ? 'has-error' : ''}`}>
                  <label htmlFor="fullName">Full Name <span>*</span></label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    placeholder="Your full name"
                    value={formData.fullName}
                    onChange={handleChange}
                  />
                  {errors.fullName && <span className="car-error">{errors.fullName}</span>}
                </div>

                {/* Email */}
                <div className={`car-field ${errors.email ? 'has-error' : ''}`}>
                  <label htmlFor="email">Email Address <span>*</span></label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                  />
                  {errors.email && <span className="car-error">{errors.email}</span>}
                </div>

                {/* Phone */}
                <div className={`car-field ${errors.phone ? 'has-error' : ''}`}>
                  <label htmlFor="phone">Phone Number <span>*</span></label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    placeholder="10-digit mobile number"
                    value={formData.phone}
                    onChange={handleChange}
                  />
                  {errors.phone && <span className="car-error">{errors.phone}</span>}
                </div>

                {/* City */}
                <div className={`car-field ${errors.city ? 'has-error' : ''}`}>
                  <label htmlFor="city">Current City <span>*</span></label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    placeholder="e.g. Hyderabad"
                    value={formData.city}
                    onChange={handleChange}
                  />
                  {errors.city && <span className="car-error">{errors.city}</span>}
                </div>

                {/* Position */}
                <div className={`car-field ${errors.position ? 'has-error' : ''}`}>
                  <label htmlFor="position">Position Applying For <span>*</span></label>
                  <select
                    id="position"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                  >
                    <option value="">Select a position</option>
                    {JOBS.map((j) => (
                      <option key={j.id} value={j.title}>{j.title}</option>
                    ))}
                  </select>
                  {errors.position && <span className="car-error">{errors.position}</span>}
                </div>

                {/* Experience */}
                <div className={`car-field ${errors.experience ? 'has-error' : ''}`}>
                  <label htmlFor="experience">Years of Experience <span>*</span></label>
                  <input
                    type="text"
                    id="experience"
                    name="experience"
                    placeholder="e.g. 2 years"
                    value={formData.experience}
                    onChange={handleChange}
                  />
                  {errors.experience && <span className="car-error">{errors.experience}</span>}
                </div>

                {/* Current Company */}
                <div className="car-field">
                  <label htmlFor="company">Current / Previous Company</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    placeholder="Company name (optional)"
                    value={formData.company}
                    onChange={handleChange}
                  />
                </div>

                {/* LinkedIn */}
                <div className="car-field">
                  <label htmlFor="linkedin">LinkedIn Profile</label>
                  <input
                    type="url"
                    id="linkedin"
                    name="linkedin"
                    placeholder="https://linkedin.com/in/yourprofile (optional)"
                    value={formData.linkedin}
                    onChange={handleChange}
                  />
                </div>

                {/* About — full width */}
                <div className={`car-field car-field-full ${errors.about ? 'has-error' : ''}`}>
                  <label htmlFor="about">Brief About Yourself <span>*</span></label>
                  <textarea
                    id="about"
                    name="about"
                    rows={4}
                    placeholder="Tell us a little about yourself, your goals, and why you want to join VahaanBazar…"
                    value={formData.about}
                    onChange={handleChange}
                  />
                  {errors.about && <span className="car-error">{errors.about}</span>}
                </div>

                {/* Resume Upload — full width */}
                <div className={`car-field car-field-full ${errors.resume ? 'has-error' : ''}`}>
                  <label>Resume / CV <span>*</span></label>
                  <label className={`car-file-label ${resumeName ? 'has-file' : ''}`} htmlFor="resume">
                    <div className="car-file-icon">
                      <i className={resumeName ? 'fas fa-file-check' : 'fas fa-cloud-upload-alt'}></i>
                    </div>
                    <div className="car-file-text">
                      {resumeName ? (
                        <>
                          <strong>{resumeName}</strong>
                          <span>Click to change file</span>
                        </>
                      ) : (
                        <>
                          <strong>Click to upload your resume</strong>
                          <span>PDF or Word document, max 5 MB</span>
                        </>
                      )}
                    </div>
                  </label>
                  <input
                    type="file"
                    id="resume"
                    name="resume"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                  {errors.resume && <span className="car-error">{errors.resume}</span>}
                </div>

              </div>

              <div className="car-form-footer">
                <p className="car-form-note">
                  <i className="fas fa-lock"></i> Your information is kept confidential and used solely for recruitment purposes.
                </p>
                <button
                  type="submit"
                  className={`car-btn car-btn-dark car-submit-btn ${submitting ? 'loading' : ''}`}
                  disabled={submitting}
                >
                  {submitting ? (
                    <><i className="fas fa-spinner fa-spin"></i> Submitting…</>
                  ) : (
                    <><i className="fas fa-paper-plane"></i> Submit Application</>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className="car-foot">
        <div className="car-foot-inner">
          <div className="car-foot-brand">
            <img src="https://vahaan-buy-and-sell-category-images.s3.ap-south-1.amazonaws.com/VahaanBazar_Image.png" alt="VahaanBazar" />
            <p>India's Complete Used Automobile Marketplace</p>
            <div className="car-foot-apps">
              <a href="https://play.google.com/store/apps/details?id=com.vahaanbazar.app" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-google-play"></i> Google Play
              </a>
              <a href="https://apps.apple.com/in/app/vahaanbazar/id6753955870" target="_blank" rel="noopener noreferrer">
                <i className="fab fa-apple"></i> App Store
              </a>
            </div>
          </div>
          <div className="car-foot-col">
            <h4>Contact HR</h4>
            <p><i className="fas fa-envelope"></i> <a href="mailto:careers@vahaanbazar.com">careers@vahaanbazar.com</a></p>
            <p><i className="fas fa-phone-alt"></i> <a href="tel:+919840469298">+91 9840469298</a></p>
            <p><i className="fas fa-globe"></i> <a href="https://www.vahaanbazar.com" target="_blank" rel="noopener noreferrer">www.vahaanbazar.com</a></p>
            <p><i className="fas fa-map-marker-alt"></i> Hyderabad, India</p>
          </div>
          <div className="car-foot-col">
            <h4>Quick Links</h4>
            <p><a href="/">Home</a></p>
            <p><a href="/franchise">Franchise</a></p>
            <p><a href="/b2b">B2B / CP</a></p>
            <p><a href="/privacy-policy">Privacy Policy</a></p>
          </div>
          <div className="car-foot-col">
            <h4>Follow Us</h4>
            <div className="car-foot-socials">
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer"><i className="fab fa-linkedin-in"></i></a>
              <a href="https://instagram.com" target="_blank" rel="noopener noreferrer"><i className="fab fa-instagram"></i></a>
              <a href="https://facebook.com" target="_blank" rel="noopener noreferrer"><i className="fab fa-facebook-f"></i></a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer"><i className="fab fa-twitter"></i></a>
              <a href="https://youtube.com" target="_blank" rel="noopener noreferrer"><i className="fab fa-youtube"></i></a>
            </div>
          </div>
        </div>
        <div className="car-foot-bar">
          <p>&copy; 2026 Vahaan Bazar Private Limited. All Rights Reserved.</p>
          <div>
            <a href="/privacy-policy">Privacy Policy</a>
            <a href="/terms-and-conditions">Terms of Service</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default CareersPage;
